import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@adsk/alloy-react-button';
import FormField from '@adsk/alloy-react-form-field';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import Panel from '@adsk/alloy-react-panel';
import TextInput from '@adsk/alloy-react-text-input';
import TextArea from '@adsk/alloy-react-textarea';

import { useCostContext } from './CostProvider';
import { COST_TYPE } from '../helpers/consts';
import costService from '../helpers/costSdk';
import { customizedType } from '../helpers/localeHelper';

const Create = ({
  projectId,
  onClose,
  open,
  floatingPanel,
  onCreateSuccess,
  sourceEntityType,
  sourceEntityNumber,
  initialFields,
  type = '',
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const { terminologies } = useCostContext();
  const { name: initialName, description: initialDescription } =
    initialFields || {};

  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isCreating, setIsCreating] = useState(false);

  const handleOnClose = useCallback(() => {
    setName(initialName);
    setDescription(initialDescription);
    onClose();
  }, [onClose, initialName, initialDescription]);

  const handleOnCreate = async () => {
    try {
      setIsCreating(true);
      const entity = await costService.create(
        projectId,
        {
          name,
          description,
          sourceType: sourceEntityType && sourceEntityType.toUpperCase(),
          sourceRef: sourceEntityNumber,
        },
        type,
      );
      onCreateSuccess(entity);
      setIsCreating(false);
      showNotification({
        status: NOTIFICATION_STATUSES.SUCCESS,
        message: T.translate('CREATE_SUCCESS', {
          name: `${entity.number}-${entity.name}`,
          type: customizedType(T, type, terminologies),
        }),
      });
      handleOnClose();
    } catch (err) {
      console.error(err);
      setIsCreating(false);
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: T.translate('CREATE_FAILED', {
          type: customizedType(T, type, terminologies),
        }),
      });
    }
  };

  const handleNameChange = useCallback((e) => {
    setName(e.target.value);
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    setDescription(e.target.value);
  }, []);

  return (
    <Panel
      onClose={handleOnClose}
      title={T.translate('CREATE_CHANGE_ORDER', {
        type: customizedType(T, type, terminologies),
      })}
      open={open}
      floating={floatingPanel}
      renderFooter={({ style }) => (
        <div style={style}>
          <Button
            size={Button.SIZES.LARGE}
            variant={Button.VARIANTS.SECONDARY}
            onClick={handleOnCreate}
            disabled={!(name && name.trim()) || isCreating}
            style={{
              width: '100%',
            }}
          >
            {T.translate('CREATE')}
          </Button>
        </div>
      )}
    >
      <Panel.Body>
        <FormField
          label={T.translate('NAME')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
          style={{ marginBottom: 16 }}
        >
          <TextInput value={name} onChange={handleNameChange} />
        </FormField>
        <FormField
          label={T.translate('DESCRIPTION')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <TextArea
            minHeight={100}
            value={description}
            onChange={handleDescriptionChange}
            style={{
              width: '100%',
            }}
          />
        </FormField>
      </Panel.Body>
    </Panel>
  );
};

Create.displayName = 'Create';

Create.propTypes = {
  /**
   * project id
   */
  projectId: PropTypes.string,
  /**
   * Callback that's being called when close the modal.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * The open state of the panel.
   */
  open: PropTypes.bool,
  /**
   * Whether the panel is overlaying content or not.
   */
  floatingPanel: PropTypes.bool,
  /**
   * Callback that's being called when create PCO successfully.
   */
  onCreateSuccess: PropTypes.func.isRequired,
  /**
   * The source entity type to create the PCO
   */
  sourceEntityType: PropTypes.string,
  /**
   * The user friendly number that try to linked the created PCO
   */
  sourceEntityNumber: PropTypes.string,

  /*
    The initial fields of PCO
    */
  initialFields: PropTypes.shape({
    /**
     * The initial name of PCO
     */
    name: PropTypes.string,
    /**
     * The initial description of pco
     */
    description: PropTypes.string,
  }),
  /**
   * The cost type, should be one of 'pco', 'oco', 'sco', 'rfq', 'rco','budget'
   */
  type: PropTypes.oneOf(COST_TYPE).isRequired,
};

export default Create;
